import {LitElement, html, css, nothing} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {Activity} from './activity';
import {IQueueable} from './interfaces/queueable';
import global_css from './global-css';
import {ToolTippy} from './mixins/tooltippy';
import {formatCostEffect, formatTime} from './utils';
import Decimal from './break_infinity';

@customElement('ri-activity-queue-item')
export class ActivityQueueItem extends ToolTippy(LitElement, {
  placement: 'top-start',
}) {
  activity: IQueueable = new Activity();
  @property({type: Number}) repeat = 1;
  get totalLength() {
    return this.activity ? this.repeat * this.activity?.length : 0;
  }
  @property({type: Number}) activeIndex = 0;
  @property({type: Boolean, reflect: true}) active = false;
  @property() option = '';
  get optionIndex() {
    return this.activity?.options?.findIndex((o) => o === this.option);
  }
  tooltipContent: () => string = this._tooltipContent;
  _tooltipContent() {
    if (this.activity.options.length > 0 && this.option) {
      return formatCostEffect(
        this.activity.optionData[this.option],
        this.repeat
      );
    }
    return formatCostEffect(this.activity, this.repeat);
  }

  static override styles = [
    global_css,
    css`
      :host {
        width: 100%;
        background-color: rgb(var(--value-color, initial), 0.2);
      }

      [active] {
        background-color: #fff8;
      }

      .queue-item,
      .drag-helper {
        display: flex;
        width: 100%;
        justify-content: space-between;
        transition: background-color 0.1s ease-in-out;
      }

      .queue-item > *,
      .drag-helper > * {
        padding: 4px;
      }

      .queue-item smooth-progress,
      .drag-helper smooth-progress,
      .queue-item progress,
      .drag-helper progress {
        width: 25%;
      }

      .queue-item-label {
        width: 50%;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .queue-item-label-header {
        min-width: 60%;
      }

      .drag-hover {
        background: #0002;
      }

      .drag-handle {
        padding: 2px;
        padding-top: 5px; // Manually centering braille icon. Ew.
      }

      label {
        font-size: 1.5em;
      }

      .repeat {
        font-size: 1.25em;
        padding: 4px;
      }
      .repeat::before {
        content: 'x';
        padding-right: 2px;
      }
    `,
  ];

  constructor() {
    super();
    setTimeout(() => {
      this.classList.add(this.activity.name);
    });
  }

  override render() {
    return html`
      <juicy-draggable
        position=${'static'}
        dropselector=${'ri-activity-queue-item'}
        @juicy-draggable-start=${this._onReorderStart}
        @juicy-draggable-move=${this._onReorderMove}
        @juicy-draggable-stop=${this._onReorderDrop}
      >
        <div class="queue-item" ?active=${this.active}>
          <div class="drag-handle">⠿</div>
          <div class="queue-item-label">
            <div class="queue-item-label-header">
              <label
                class="fancy ${this.activity.name} ${[...this.activity.tags]
                  .filter((tag) => tag.includes('Magic'))
                  .join(' ')}"
                >${this.activity?.displayName}</label
              >
              ${this.activity?.options?.length > 0
                ? html`
                    <select
                      name="options"
                      class="fancy ${this.option}"
                      @change=${(e) =>
                        (this.option =
                          this.activity?.options?.[e.target.value])}
                    >
                      ${this.activity?.options.map(
                        (option, index) => html`
                          <option
                            value=${index}
                            class=${this.activity?.options[index]}
                            ?selected=${index ==
                            this.activity.options.findIndex(
                              (o) => o === this.option
                            )}
                          >
                            ${option}
                          </option>
                        `
                      )}
                    </select>
                  `
                : nothing}
            </div>
            <div class="nowrap">
              <span class="repeat">${this.repeat}</span>
              <button
                @mousemove=${(event: Event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                @click=${this._onMore}
              >
                +
              </button>
              <button
                @mousemove=${(event: Event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                @click=${this._onLess}
              >
                -
              </button>
            </div>
          </div>
          <smooth-progress
            class="${this.activity.name} ${[...this.activity.tags]
              .filter((tag) => tag.includes('Magic'))
              .join(' ')}"
            .value=${new Decimal(this.activeIndex)}
            .max=${new Decimal(this.totalLength)}
            .text=${`${formatTime(this.activeIndex)} /
              ${formatTime(this.totalLength)}`}
          >
          </smooth-progress>
          <div>
            <button
              @mousemove=${(event: Event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              @click=${this._onEarlier}
            >
              ▲
            </button>
            <button
              @mousemove=${(event: Event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              @click=${this._onLater}
            >
              ▼
            </button>
            <button
              @mousemove=${(event: Event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              @click=${this._onSplit}
            >
              ⟷
            </button>
            <button
              @mousemove=${(event: Event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              @click=${this._onDelete}
            >
              🗑
            </button>
          </div>
        </div>
      </juicy-draggable>
    `;
  }

  private _onReorderStart(_: Event) {
    setTimeout(() => {
      const queueItem = this.shadowRoot?.querySelector(
        '.queue-item'
      ) as HTMLElement;
      if (queueItem) queueItem.style.visibility = 'hidden';
      const dragHelper = this.shadowRoot?.querySelector(
        '.drag-helper'
      ) as HTMLElement;
      if (dragHelper) dragHelper.style.visibility = 'visible';
    });
  }

  private _onReorderMove(_: Event) {}

  private _onReorderDrop(e: CustomEvent) {
    setTimeout(() => {
      const queueItem = this.shadowRoot?.querySelector(
        '.queue-item'
      ) as HTMLElement;
      if (queueItem) queueItem.style.visibility = 'visible';
      const dragHelper = this.shadowRoot?.querySelector(
        '.drag-helper'
      ) as HTMLElement;
      if (dragHelper) dragHelper.style.visibility = 'hidden';
      const drop = e.detail.dropElement;
      if (drop) {
        const event = new CustomEvent('queue-item-reorder', {
          detail: {
            from: Object.values(this.parentNode?.children ?? {}).indexOf(this),
            to: Object.values(this.parentNode?.children ?? {}).indexOf(drop),
          },
          bubbles: true,
          composed: true,
        });
        this.dispatchEvent(event);
      }
    });
  }

  private _onMore(e: MouseEvent) {
    let numClicks = 1;
    if (e.ctrlKey) numClicks *= 5;
    if (e.shiftKey) numClicks *= 10;
    this.repeat += numClicks;
  }
  private _onLess(e: MouseEvent) {
    let numClicks = 1;
    if (e.ctrlKey) numClicks *= 5;
    if (e.shiftKey) numClicks *= 10;
    this.repeat -= numClicks;
    if (this.repeat < 0) this.repeat = 0;
  }

  private _onEarlier() {
    const event = new Event('queue-item-earlier', {
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  private _onLater() {
    const event = new Event('queue-item-later', {
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  private _onSplit() {
    const event = new Event('queue-item-split', {
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  private _onDelete() {
    const event = new Event('queue-item-deleted', {
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }
}
